import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { showSaveFailedToast } from "@/lib/saveFailureToast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { usePreferences } from "@/hooks/useUserPreferences";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KID_COLOR_PALETTE, type KidColorRecord } from "@/lib/kidColors";
import {
  clearRemovedKid,
  recordRemovedKid,
  recordRenamedKid,
} from "@/lib/removedKidLedger";
import {
  archiveFactsForKid,
  renameFactSubjectForKid,
  unlinkProvidersForKid,
} from "@/lib/kidFactCleanup";
import { reportError } from "@/lib/reportError";
import { isUnverifiedFactWrite, rememberFamilyFact, undoFamilyFact } from "@/lib/rememberFamilyFact";
import { familyFactUnverifiedCopy } from "@/lib/familyFactAddCopy";
import {
  KID_GENDER_FACT_KEY,
  KID_GENDER_LABELS,
  KID_GENDER_VALUES,
  normalizeKidGender,
  type KidGender,
} from "@/lib/kidGender";

/** Trim a kid's name, preserving the exact casing the user typed. */
function cleanName(raw: string): string {
  return String(raw || "").trim();
}

/** Case-insensitive lookup. Mirrors lookupPersistedKidColor in TodayTab. */
function findColor(name: string, colors: KidColorRecord): string | null {
  if (colors[name]) return colors[name];
  const lc = name.toLowerCase();
  for (const k of Object.keys(colors)) {
    if (k.toLowerCase() === lc) return colors[k];
  }
  return null;
}

/** First color in the stable palette that isn't already assigned. */
function pickAvailableColor(existing: KidColorRecord): string {
  const used = new Set(Object.values(existing));
  for (const c of KID_COLOR_PALETTE) {
    if (!used.has(c)) return c;
  }
  return KID_COLOR_PALETTE[Object.keys(existing).length % KID_COLOR_PALETTE.length];
}

/** Format a YYYY-MM-DD birthdate as "Mar 12, 2020 · age 6". */
function formatBirthdate(isoDate: string): string {
  const [yr, mo, dy] = isoDate.split("-").map(Number);
  const monthName = new Date(yr, mo - 1, dy).toLocaleString("en-US", { month: "short" });
  const today = new Date();
  let age = today.getFullYear() - yr;
  if (
    today.getMonth() + 1 < mo ||
    (today.getMonth() + 1 === mo && today.getDate() < dy)
  ) {
    age--;
  }
  return `${monthName} ${dy}, ${yr} · age ${age}`;
}

/** "My Kids" Settings section: list / add / rename / remove kids and their
 *  stable color assignments. Reads + writes user_preferences directly.
 *  Birthday facts are read/written to family_context (additive, non-blocking). */
export default function MyKidsSection() {
  const { user } = useAuth();
  const { prefs, loading: prefsLoading, updatePreferences } = usePreferences();
  const [names, setNames] = useState<string[]>([]);
  const [colors, setColors] = useState<KidColorRecord>({});
  const [loaded, setLoaded] = useState(false);

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const newInputRef = useRef<HTMLInputElement>(null);

  const [editingName, setEditingName] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // Birthday state
  const [birthdates, setBirthdates] = useState<Record<string, string>>({});
  // #1227 task 3 — a failed READ must never render as "no birthday set".
  //
  // The load below used to be `if (!bdResult.error && ...)`: on a 42703 or an
  // RLS refusal it simply skipped the setState, `birthdates` stayed {}, and
  // every kid rendered the "Add birthday" prompt. That is indistinguishable
  // from an account that genuinely has none — which is exactly how #1210 hid
  // for seven weeks on the sibling surface. Three states, not two: loading,
  // loaded-and-empty, and failed.
  //
  // ⚠️ #1853 widened the single query below to fetch birthdates AND genders
  // together, so this ONE flag covers both — they succeed or fail as one round
  // trip, and a second flag would be provably identical to this one. The
  // gender toggle reads it for exactly the same reason the birthday line does:
  // an unselected toggle over a failed read invites her to re-pick a value she
  // already set, and then "Saved!" would confirm a change that wasn't one.
  const [factsFailed, setFactsFailed] = useState(false);
  const [birthdateDraftFor, setBirthdateDraftFor] = useState<string | null>(null);
  const [birthdateDraft, setBirthdateDraft] = useState("");
  const birthdateInputRef = useRef<HTMLInputElement>(null);

  // #1853 — gender state. Three states per kid, and the third is the point:
  // a name absent from `genders` has no gender recorded, which renders as
  // NEITHER button selected and tells Kin to say "they".
  const [genders, setGenders] = useState<Record<string, KidGender>>({});
  // Row ids, kept so clearing a gender can archive the exact row.
  //
  // ⚠️ Needed because clearing is an ARCHIVE, not a write: upsert_family_fact
  // rejects an empty value (`rejected_empty`), so there is no "write nothing"
  // path, and `undoFamilyFact` takes an id. The alternative — matching on
  // category+key+subject — is the matcher that archives sibling rows for
  // multi-valued keys, called out in rememberFamilyFact.ts. Ids, not matchers.
  const [genderFactIds, setGenderFactIds] = useState<Record<string, string>>({});
  // Which kid's toggle is mid-write. Guards against a double tap racing itself
  // into two writes whose results arrive out of order.
  const [savingGenderFor, setSavingGenderFor] = useState<string | null>(null);

  // Load on mount — initialize names/colors from shared context; fetch birthdates from DB.
  useEffect(() => {
    if (!user || prefsLoading) return;
    let cancelled = false;
    (async () => {
      try {
        // Initialize kid names and colors from context (no extra DB call needed).
        const rawNames = prefs.known_kid_names;
        setNames(rawNames.filter((n) => typeof n === "string" && n.trim().length > 0));
        setColors(
          prefs.known_kid_colors && typeof prefs.known_kid_colors === "object" && !Array.isArray(prefs.known_kid_colors)
            ? (prefs.known_kid_colors as KidColorRecord)
            : {}
        );

        // #1853 — ONE query for both identity facts, not two.
        //
        // `id` joins the select list because clearing a gender archives that
        // exact row (see genderFactIds). It is NOT the selected-then-discarded
        // trap of #1696: the gender branch below consumes it.
        const bdResult = await (supabase as any)
          .from("family_context")
          .select("id, subject, key, value")
          .eq("user_id", user.id)
          .eq("category", "kid")
          .in("key", ["birthdate", KID_GENDER_FACT_KEY])
          .eq("archived", false);

        if (cancelled) return;

        if (bdResult.error || !Array.isArray(bdResult.data)) {
          // Reported, not swallowed. #1227 task 2 routed these to Sentry; the
          // point here is the UI consequence, which is separate.
          console.error("[MyKids] kid facts read failed", bdResult.error);
          setFactsFailed(true);
        } else {
          const map: Record<string, string> = {};
          const genderMap: Record<string, KidGender> = {};
          const idMap: Record<string, string> = {};
          for (const row of bdResult.data) {
            if (!row.subject) continue;
            if (row.key === "birthdate") {
              if (row.value) map[row.subject] = row.value;
              continue;
            }
            if (row.key === KID_GENDER_FACT_KEY) {
              // ⚠️ Through normalizeKidGender, not a raw assignment. A row
              // holding anything other than "boy"/"girl" — a stale value, a
              // future third option, a hand-edited row — must read as NOT SET
              // rather than light up a button for a value this UI cannot
              // represent. The id is still kept so she can clear it.
              const g = normalizeKidGender(row.value);
              if (g) genderMap[row.subject] = g;
              if (row.id) idMap[row.subject] = String(row.id);
            }
          }
          setBirthdates(map);
          setGenders(genderMap);
          setGenderFactIds(idMap);
          setFactsFailed(false);
        }

        setLoaded(true);
      } catch (err) {
        console.error("[MyKids] load threw", err);
        // A thrown read is a failed read. Landing in the empty state here is
        // the same defect as the branch above, one level out.
        if (!cancelled) setFactsFailed(true);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, prefsLoading]);

  // Focus the add-input when it opens.
  useEffect(() => {
    if (adding) {
      setTimeout(() => newInputRef.current?.focus(), 50);
    }
  }, [adding]);

  // Focus the edit-input when it opens.
  useEffect(() => {
    if (editingName) {
      setTimeout(() => editInputRef.current?.select(), 50);
    }
  }, [editingName]);

  // Focus the birthdate input when it opens.
  useEffect(() => {
    if (birthdateDraftFor) {
      setTimeout(() => birthdateInputRef.current?.focus(), 50);
    }
  }, [birthdateDraftFor]);

  /**
   * Write the kid list, and report honestly whether it landed.
   *
   * This used to be a try/catch returning false in the catch. updatePreferences
   * RESOLVES its PostgrestError rather than throwing (#416), so that catch
   * could never fire and **persist returned true unconditionally** — every
   * caller does `if (ok) toast.success(...)`, so all three confirmations
   * ("Added Carson 💜", "Removed Carson", "Updated") printed over writes that
   * may never have happened, and the failure branch had never once run (#545).
   *
   * prev* are the values as they were BEFORE the caller's optimistic setState,
   * so a refused write is rolled back to exactly what she had. Field-scoped by
   * construction: only the two keys this write touches are restored, so
   * anything else that changed mid-flight survives (#438/#580).
   */
  const persist = useCallback(
    async (
      nextNames: string[],
      nextColors: KidColorRecord,
      prevNames: string[],
      prevColors: KidColorRecord
    ) => {
      const error = await updatePreferences({
        known_kid_names: nextNames,
        known_kid_colors: nextColors as any,
      });
      if (error) {
        // failure reports must be error-level: Sentry captures only "error" (see main.tsx captureConsoleIntegration).
        reportError("[MyKids] kid_names save failed", error, {
          tags: { area: "my-kids", writer: "kid_names" },
          extra: { code: (error as any)?.code, details: (error as any)?.details, hint: (error as any)?.hint },
        });
        setNames(prevNames);
        setColors(prevColors);
        showSaveFailedToast();
        return false;
      }
      return true;
    },
    [updatePreferences]
  );

  const handleAdd = useCallback(async () => {
    const name = cleanName(newName);
    if (!name) return;
    if (names.some((n) => n.toLowerCase() === name.toLowerCase())) {
      toast.error(`${name} is already in your kids list`);
      return;
    }
    const nextNames = [...names, name];
    const nextColors: KidColorRecord = { ...colors };
    if (!findColor(name, nextColors)) {
      nextColors[name] = pickAvailableColor(nextColors);
    }
    setNames(nextNames);
    setColors(nextColors);
    setNewName("");
    setAdding(false);
    const ok = await persist(nextNames, nextColors, names, colors);
    if (!ok) return;
    // #1243 — adding a kid of this name back clears her ledger entry, so Kin
    // treats her normally again. This is an EXPLICIT user action, which is the
    // only kind that may clear: `TodayTab.harvestKidsFromPlan` also writes
    // known_kid_names, from names it INFERS off an imported photo, and a clear
    // keyed off that would let a school-calendar screenshot resurrect a removed
    // child. Kasandra scoped this "only on an explicit user action".
    //
    // After the persist, not before: if the kid write is refused she is not in
    // the list, and clearing the ledger would put her back in Kin's mouth while
    // the UI shows her gone. Failure here is logged, not toasted — the add DID
    // succeed, and the honest receipt for that is "Added".
    let ledgerCleared = true;
    if (user) {
      try {
        await clearRemovedKid(supabase, user.id, name);
      } catch (e) {
        ledgerCleared = false;
        console.error("[family-memory] clearing removed-kid ledger failed", e);
      }
    }
    if (ledgerCleared) {
      toast.success(`Added ${name} 💜`);
    } else {
      // ⚠️ NOT toast.success. She has added this child back, but Kin is still
      // holding an instruction not to treat them as hers — so Kin would keep
      // sidestepping the name she just typed in, with nothing on screen saying
      // why. The add DID land, so this does not undo it or read as a failure;
      // it says what is true. Silence here would be the confirmation-over-an-
      // unverified-write pattern one level out.
      toast.error(
        `Added ${name}, but Kin may still avoid the name — try removing and adding them again.`,
      );
    }
  }, [newName, names, colors, persist, user]);

  const handleRemove = useCallback(
    async (name: string) => {
      const lc = name.toLowerCase();
      const nextNames = names.filter((n) => n.toLowerCase() !== lc);
      const nextColors: KidColorRecord = {};
      for (const [k, v] of Object.entries(colors)) {
        if (k.toLowerCase() !== lc) nextColors[k] = v;
      }
      setNames(nextNames);
      setColors(nextColors);
      setConfirmRemove(null);
      const ok = await persist(nextNames, nextColors, names, colors);
      if (!ok) return;

      // #1214 — this used to be fire-and-forget, with the success toast firing
      // on the preferences write alone. Removing a kid is a trust action: a mom
      // told Kin to forget a child and got "Removed Sarah" while every stored
      // fact about that child stayed live and kin-chat went on using them. The
      // archive is awaited now, and its failure is hers to see.
      if (!user) {
        toast.success(`Removed ${name}`);
        return;
      }
      try {
        await archiveFactsForKid(supabase, user.id, name);
        await unlinkProvidersForKid(supabase, user.id, name);
        // #1243 — archiving her facts does not retract HISTORY. Conversation
        // history is sent to kin-chat in the request body, so resuming an old
        // thread that names her puts her straight back in the model's context.
        // The ledger is what tells kin-chat not to. Inside the same try as the
        // archive, deliberately: if this fails the removal is partial, and the
        // catch below already says exactly that rather than claiming success.
        await recordRemovedKid(supabase, user.id, name);
        setBirthdates((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
        // #1853 — the gender row is archived by archiveFactsForKid above (it
        // filters category='kid' with no key filter, so it catches every
        // identity fact). This drops the local copy so the row disappears from
        // the list without waiting for a refetch.
        setGenders((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
        setGenderFactIds((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
        toast.success(`Removed ${name}`);
      } catch (e) {
        // The name IS out of her kids list at this point, so this is a partial
        // removal, not a no-op. Say exactly that rather than claiming success
        // or implying nothing happened.
        console.error("[family-memory] archive facts for removed kid failed", e);
        toast.error(
          `${name} is off your kids list, but Kin couldn't clear what she'd remembered. Try removing again?`
        );
      }
    },
    [names, colors, persist, user]
  );

  const handleRename = useCallback(
    async (oldName: string) => {
      const next = cleanName(editValue);
      if (!next) {
        setEditingName(null);
        return;
      }
      if (next.toLowerCase() === oldName.toLowerCase()) {
        // No change, just close.
        setEditingName(null);
        return;
      }
      if (names.some((n) => n.toLowerCase() === next.toLowerCase())) {
        toast.error(`${next} is already in your kids list`);
        return;
      }
      const lcOld = oldName.toLowerCase();
      const nextNames = names.map((n) => (n.toLowerCase() === lcOld ? next : n));
      const nextColors: KidColorRecord = {};
      for (const [k, v] of Object.entries(colors)) {
        if (k.toLowerCase() === lcOld) {
          nextColors[next] = v; // transfer color
        } else {
          nextColors[k] = v;
        }
      }
      setNames(nextNames);
      setColors(nextColors);
      setEditingName(null);
      const ok = await persist(nextNames, nextColors, names, colors);
      if (!ok) return;

      // #1214 — same three defects as handleRemove: the subject match was
      // case-sensitive against a column nothing normalizes, the write was
      // fire-and-forget, and its result was never read. A rename left
      // differently-cased rows stranded under the old name, where nothing
      // reads them and a later removal cannot find them either.
      if (!user) {
        toast.success("Updated");
        return;
      }
      try {
        await renameFactSubjectForKid(supabase, user.id, oldName, next);
        // #1243 — the OLD name is still all over her history. A rename is not a
        // removal (the child still exists), so this records `renamed_to` and
        // kin-chat renders "previously called Sarah, now called Sara" instead
        // of telling her that her own daughter is no longer in the family.
        await recordRenamedKid(supabase, user.id, oldName, next);
        setBirthdates((prev) => {
          if (!prev[oldName]) return prev;
          const updated = { ...prev };
          updated[next] = updated[oldName];
          delete updated[oldName];
          return updated;
        });
        // #1853 — renameFactSubjectForKid above moves EVERY category='kid' row
        // to the new subject, gender included, so the database is already
        // right. These two move the local copies so the toggle stays lit under
        // the new name instead of blanking until the next load — which would
        // read as the rename having lost it.
        setGenders((prev) => {
          if (!prev[oldName]) return prev;
          const updated = { ...prev };
          updated[next] = updated[oldName];
          delete updated[oldName];
          return updated;
        });
        setGenderFactIds((prev) => {
          if (!prev[oldName]) return prev;
          const updated = { ...prev };
          updated[next] = updated[oldName];
          delete updated[oldName];
          return updated;
        });
        toast.success("Updated");
      } catch (e) {
        console.error("[family-memory] rename subject failed", e);
        toast.error(
          `Renamed to ${next}, but Kin couldn't move what she'd remembered under the old name. Try again?`
        );
      }
    },
    [editValue, names, colors, persist, user]
  );

  const startEdit = (name: string) => {
    setBirthdateDraftFor(null);
    setEditValue(name);
    setEditingName(name);
  };

  const openBirthdatePicker = (name: string) => {
    setEditingName(null);
    setBirthdateDraft(birthdates[name] ?? "");
    setBirthdateDraftFor(name);
  };

  const cancelBirthdatePicker = () => {
    setBirthdateDraftFor(null);
    setBirthdateDraft("");
  };

  const handleSaveBirthdate = useCallback(
    async (kidName: string) => {
      if (!user || !birthdateDraft) {
        cancelBirthdatePicker();
        return;
      }
      const newDate = birthdateDraft; // 'YYYY-MM-DD'
      const existing = birthdates[kidName];

      // #1482 — one RPC, not a select-then-write branch.
      //
      // This chose insert-vs-update from `birthdates[kidName]`, which is local
      // component STATE, not a fresh read. When that state was stale or had not
      // hydrated yet it took the insert branch over a row that already existed,
      // and `family_context_fact_uidx` makes `birthdate` single-valued — so the
      // write 23505'd and her correction was dropped while the OLD date stayed.
      //
      // upsert_family_fact decides server-side in one statement, so the stale
      // read cannot cause a wrong branch, and it records the old value in
      // metadata.previous_value the way the update branch used to by hand.
      //
      // NOT fixable with .upsert({ onConflict }) — expression index
      // (coalesce(subject,'')) with a partial WHERE, so PostgREST answers 42P10.
      // See src/lib/rememberFamilyFact.ts.
      const result = await rememberFamilyFact(
        { category: "kid", subject: kidName, key: "birthdate", value: newDate },
        "manual",
        "high",
      );

      // #1482 — house rule 16. The toast and the local-state update used to run
      // unconditionally: a failed write reported to Sentry and STILL printed
      // "Birthday saved 🎂" while the row kept its old date, and the list showed
      // the new one. A false confirmation is worse than silence, because
      // silence makes her check. Both are now gated on the write landing.
      if (!result.ok) {
        // failure reports must be error-level: Sentry captures only "error" (see main.tsx captureConsoleIntegration).
        // #1857 — "unverified" is the helper's third state: the response was
        // lost and the read-back could not answer. Titled apart from a real
        // failure so Sentry does not file "save failed" over a fact that may
        // well be saved.
        reportError(
          `[MyKids] kid_birthday save ${isUnverifiedFactWrite(result) ? "unverified" : "failed"}`,
          Object.assign(
            new Error(
              `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
            ),
            { code: result.action ?? "no_action_row", details: result.error },
          ),
          {
            tags: { area: "my-kids", writer: "kid_birthday" },
            extra: { code: result.action ?? "no_action_row", details: result.error },
          },
        );
        // Deliberately does not say "try again": #1481 is the open issue about
        // exactly that advice being unusable when a retry cannot succeed. This
        // says what happened and leaves the old date visible and correct.
        // #1857 — "didn't save" is a claim about the database. On the third
        // state the honest sentence is "couldn't confirm", and the old date
        // stays on screen either way: showing the new one would be the
        // optimistic guess the issue warns against.
        if (isUnverifiedFactWrite(result)) {
          toast(familyFactUnverifiedCopy("that birthday"));
        } else {
          toast.error("That birthday didn't save.");
        }
        cancelBirthdatePicker();
        return;
      }

      setBirthdates((prev) => ({ ...prev, [kidName]: newDate }));
      toast.success("Birthday saved 🎂");

      cancelBirthdatePicker();
    },
    [user, birthdateDraft, birthdates]
  );

  /**
   * #1853 — set, change, or clear one kid's gender.
   *
   * Tapping the button that is already selected CLEARS it, which is the only
   * way back to "not recorded" once she has picked. Without that, a mistap is
   * permanent and Kin would keep confidently using the wrong pronoun — worse
   * than the guess this feature replaces, because now it looks deliberate.
   *
   * ⚠️ Clearing ARCHIVES the row rather than writing an empty value:
   * upsert_family_fact answers an empty value with `rejected_empty`, so there
   * is no "write nothing" path through the RPC.
   *
   * ⚠️ House rule 16 throughout. `rememberFamilyFact` RETURNS its failure
   * instead of throwing — a try/catch around it is decorative — so `result.ok`
   * is the only honest gate on the toast, and `undoFamilyFact` is read the same
   * way. The optimistic state is rolled back FIELD-SCOPED on failure: only this
   * kid's entry is restored, so another kid's change landing mid-flight
   * survives (#438/#580).
   */
  const handleSetGender = useCallback(
    async (kidName: string, picked: KidGender) => {
      if (!user || savingGenderFor) return;
      // A failed read renders every toggle unselected. Writing from that state
      // would be acting on a value we never actually loaded, and "Saved!" would
      // confirm a change she may not have made.
      if (factsFailed) return;

      const current = genders[kidName] ?? null;
      const next = current === picked ? null : picked;

      const prevGender = current;
      const prevFactId = genderFactIds[kidName];

      // Optimistic, then rolled back below if the write is refused.
      setGenders((prev) => {
        const updated = { ...prev };
        if (next) updated[kidName] = next;
        else delete updated[kidName];
        return updated;
      });
      setSavingGenderFor(kidName);

      const rollback = () => {
        setGenders((prev) => {
          const updated = { ...prev };
          if (prevGender) updated[kidName] = prevGender;
          else delete updated[kidName];
          return updated;
        });
      };

      try {
        if (next === null) {
          // ⚠️ Reaching this branch means `current` was non-null (clearing only
          // happens when she taps the pill that is already lit), so a gender IS
          // recorded and a row for it exists. No id therefore means WE lost
          // track of it, not that there is nothing to clear.
          //
          // The first cut returned silently here, "treating it as already
          // cleared". That is the exact failure this whole PR is about, one
          // level in: the pill would go dark while the row stayed `boy`, and
          // the next morning's text would keep using a pronoun the screen says
          // she removed. Silence is the worst option — it is a confirmation
          // with no words.
          //
          // So: roll back, report, and leave the TRUE state on screen. She can
          // reload and try again, and until she does the UI and the briefing
          // agree with each other.
          if (!prevFactId) {
            rollback();
            reportError(
              "[MyKids] kid_gender clear had no fact id",
              Object.assign(new Error("gender set locally but no stored row id to archive"), {
                code: "missing_fact_id",
              }),
              {
                tags: { area: "my-kids", writer: "kid_gender" },
                extra: { code: "missing_fact_id" },
              },
            );
            toast.error("That didn't save.");
            return;
          }
          const ok = await undoFamilyFact(prevFactId);
          if (!ok) {
            rollback();
            reportError(
              "[MyKids] kid_gender clear failed",
              Object.assign(new Error("undoFamilyFact returned false"), {
                code: "undo_failed",
              }),
              {
                tags: { area: "my-kids", writer: "kid_gender" },
                extra: { code: "undo_failed" },
              },
            );
            toast.error("That didn't save.");
            return;
          }
          setGenderFactIds((prev) => {
            const updated = { ...prev };
            delete updated[kidName];
            return updated;
          });
          toast.success("Saved!");
          return;
        }

        const result = await rememberFamilyFact(
          { category: "kid", subject: kidName, key: KID_GENDER_FACT_KEY, value: next },
          "manual",
          "high",
        );

        if (!result.ok) {
          rollback();
          // failure reports must be error-level: Sentry captures only "error" (see main.tsx captureConsoleIntegration).
          reportError(
            `[MyKids] kid_gender save ${isUnverifiedFactWrite(result) ? "unverified" : "failed"}`,
            Object.assign(
              new Error(
                `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
              ),
              { code: result.action ?? "no_action_row", details: result.error },
            ),
            {
              tags: { area: "my-kids", writer: "kid_gender" },
              extra: { code: result.action ?? "no_action_row", details: result.error },
            },
          );
          // Same reasoning as handleSaveBirthdate: no "try again", because
          // #1481 is the open issue about that advice being unusable when a
          // retry cannot succeed. The previous value stays on screen, correct.
          // #1857 — same split as the birthday above. The pill rolls back in
          // both cases (the previous value is the last one PROVED stored), but
          // only a proven failure gets to say "didn't".
          if (isUnverifiedFactWrite(result)) {
            toast(familyFactUnverifiedCopy());
          } else {
            toast.error("That didn't save.");
          }
          return;
        }

        // ⚠️ The id only comes back on a write that touched a row. A `duplicate`
        // action returns none (the losing call never saw the winner's row), so
        // keep whatever id we already had rather than deleting a good one — a
        // missing id would make the NEXT clear a silent no-op.
        if (result.factId) {
          setGenderFactIds((prev) => ({ ...prev, [kidName]: result.factId as string }));
        }
        toast.success("Saved!");
      } finally {
        setSavingGenderFor(null);
      }
    },
    [user, savingGenderFor, factsFailed, genders, genderFactIds],
  );

  const displayList = useMemo(() => {
    return names.map((name) => ({
      name,
      color: findColor(name, colors) ?? "#5050C8",
    }));
  }, [names, colors]);

  // Today's date as YYYY-MM-DD for the date picker max attribute.
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <section className="mt-10">
      <h2
        className="font-body uppercase font-semibold mb-2"
        style={{ fontSize: "11px", letterSpacing: "0.5px", color: "#54525C" }}
      >
        My Kids
      </h2>
      <div className="h-px mb-3" style={{ backgroundColor: "#E8E8F4" }} />
      <div
        className="bg-white"
        style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}
      >
        {!loaded ? (
          <p className="font-body text-sm italic" style={{ color: "#8B8B9E" }}>
            Loading…
          </p>
        ) : displayList.length === 0 && !adding ? (
          <p className="font-body text-sm" style={{ color: "#54525C", lineHeight: 1.5 }}>
            Add your kids here and Kin will color-code their events automatically.
            You can also just mention them in your messages — Kin will try to spot
            them — but adding them here is the fastest way to make sure she gets
            it right every time.
          </p>
        ) : (
          <div className="flex flex-col" style={{ gap: 8 }}>
            {displayList.map(({ name, color }) => {
              const isEditing = editingName === name;
              const isBirthdateEditing = birthdateDraftFor === name;

              return (
                <div
                  key={name}
                  className="flex items-center"
                  style={{
                    gap: 10,
                    padding: "8px 10px",
                    backgroundColor: "#F0F0FF",
                    borderRadius: 10,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      backgroundColor: color,
                      display: "inline-block",
                      flexShrink: 0,
                      alignSelf: "flex-start",
                      marginTop: 4,
                    }}
                  />
                  {isEditing ? (
                    <>
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRename(name);
                          if (e.key === "Escape") setEditingName(null);
                        }}
                        className="font-body bg-white flex-1 min-w-0"
                        style={{
                          fontSize: 14,
                          color: "#1C1A3E",
                          border: "1px solid #C8C8F4",
                          borderRadius: 8,
                          padding: "6px 10px",
                          outline: "none",
                        }}
                      />
                      <button
                        onClick={() => handleRename(name)}
                        aria-label="Save"
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, color: "#5050C8" }}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => setEditingName(null)}
                        aria-label="Cancel"
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, color: "#54525C" }}
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : isBirthdateEditing ? (
                    <>
                      <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 4 }}>
                        <span
                          className="font-body"
                          style={{ fontSize: 14, color: "#1C1A3E" }}
                        >
                          {name}
                        </span>
                        <input
                          ref={birthdateInputRef}
                          type="date"
                          value={birthdateDraft}
                          max={todayIso}
                          onChange={(e) => setBirthdateDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveBirthdate(name);
                            if (e.key === "Escape") cancelBirthdatePicker();
                          }}
                          className="font-body bg-white"
                          style={{
                            fontSize: 12,
                            color: "#1C1A3E",
                            border: "1px solid #C8C8F4",
                            borderRadius: 6,
                            padding: "3px 6px",
                            outline: "none",
                            maxWidth: 150,
                          }}
                        />
                      </div>
                      <button
                        onClick={() => handleSaveBirthdate(name)}
                        aria-label="Save birthday"
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, color: "#5050C8" }}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={cancelBirthdatePicker}
                        aria-label="Cancel"
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, color: "#54525C" }}
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 2 }}>
                        <span
                          className="font-body truncate"
                          style={{ fontSize: 14, color: "#1C1A3E" }}
                        >
                          {name}
                        </span>
                        {factsFailed ? (
                          // ⚠️ NOT the "Add birthday" prompt, and NOT an unset
                          // gender toggle. Kin does not know whether either is
                          // stored, and inviting her to set one she may already
                          // have is how a broken read reads as her own
                          // forgetfulness.
                          <span
                            className="font-body text-left"
                            style={{ fontSize: 11, color: "#B91C1C", lineHeight: 1.4 }}
                          >
                            Couldn&rsquo;t load birthday or boy/girl
                          </span>
                        ) : (
                          <>
                            {birthdates[name] ? (
                              <button
                                onClick={() => openBirthdatePicker(name)}
                                className="font-body text-left"
                                style={{ fontSize: 11, color: "#54525C", lineHeight: 1.4 }}
                              >
                                🎂 {formatBirthdate(birthdates[name])}
                              </button>
                            ) : (
                              <button
                                onClick={() => openBirthdatePicker(name)}
                                className="font-body text-left"
                                style={{ fontSize: 11, color: "#5050C8", lineHeight: 1.4 }}
                              >
                                🎂 Add birthday
                              </button>
                            )}
                            {/* #1853 — boy/girl. Neither lit = not recorded,
                                which is a real state and the default for every
                                existing account: Kin says "they" until she
                                picks. Tapping the lit one clears it. */}
                            <div
                              className="flex items-center"
                              style={{ gap: 4, marginTop: 2 }}
                              role="group"
                              aria-label={`${name}: boy or girl`}
                            >
                              {KID_GENDER_VALUES.map((g) => {
                                const selected = genders[name] === g;
                                return (
                                  <button
                                    key={g}
                                    onClick={() => handleSetGender(name, g)}
                                    disabled={savingGenderFor === name}
                                    aria-pressed={selected}
                                    aria-label={`${KID_GENDER_LABELS[g]} — ${name}`}
                                    className="font-body"
                                    style={{
                                      fontSize: 11,
                                      lineHeight: 1.4,
                                      padding: "2px 10px",
                                      borderRadius: 999,
                                      border: `1px solid ${selected ? "#5050C8" : "#C8C8F4"}`,
                                      backgroundColor: selected ? "#5050C8" : "#FFFFFF",
                                      color: selected ? "#FFFFFF" : "#54525C",
                                      fontWeight: selected ? 600 : 400,
                                      opacity: savingGenderFor === name ? 0.6 : 1,
                                    }}
                                  >
                                    {KID_GENDER_LABELS[g]}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => startEdit(name)}
                        aria-label={`Rename ${name}`}
                        className="font-body font-semibold hover:underline"
                        style={{
                          fontSize: 12,
                          color: "#54525C",
                          padding: "4px 6px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          alignSelf: "flex-start",
                        }}
                      >
                        <Pencil size={12} />
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmRemove(name)}
                        aria-label={`Remove ${name}`}
                        className="font-body font-semibold hover:underline"
                        style={{
                          fontSize: 12,
                          color: "#54525C",
                          padding: "4px 6px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          alignSelf: "flex-start",
                        }}
                      >
                        <Trash2 size={12} />
                        Remove
                      </button>
                    </>
                  )}
                </div>
              );
            })}

            {adding && (
              <div
                className="flex items-center"
                style={{
                  gap: 10,
                  padding: "8px 10px",
                  backgroundColor: "#F0F0FF",
                  borderRadius: 10,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    backgroundColor: pickAvailableColor(colors),
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <input
                  ref={newInputRef}
                  type="text"
                  value={newName}
                  placeholder="Kid's name"
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                    if (e.key === "Escape") {
                      setNewName("");
                      setAdding(false);
                    }
                  }}
                  className="font-body bg-white flex-1 min-w-0"
                  style={{
                    fontSize: 14,
                    color: "#1C1A3E",
                    border: "1px solid #C8C8F4",
                    borderRadius: 8,
                    padding: "6px 10px",
                    outline: "none",
                  }}
                />
                <button
                  onClick={handleAdd}
                  aria-label="Save"
                  className="flex items-center justify-center rounded-full"
                  style={{ width: 28, height: 28, color: "#5050C8" }}
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => {
                    setNewName("");
                    setAdding(false);
                  }}
                  aria-label="Cancel"
                  className="flex items-center justify-center rounded-full"
                  style={{ width: 28, height: 28, color: "#54525C" }}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="font-body font-semibold mt-3 hover:underline transition-all inline-flex items-center"
            style={{ fontSize: 13, color: "#5050C8", gap: 4 }}
          >
            <Plus size={14} />
            Add a kid
          </button>
        )}
      </div>

      <AlertDialog
        open={!!confirmRemove}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmRemove}?</AlertDialogTitle>
            <AlertDialogDescription>
              Kin will forget what she's remembered about them — sizes, routines,
              the little things. Their events stay put.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRemove && handleRemove(confirmRemove)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
